import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import { Button } from 'react-bootstrap';
import ReactPlayer from 'react-player';
import TypeOfAnimationBlock from './typeOfAnimationBlock';
import { Reorder } from "framer-motion";
import { useHookContext } from './hookContext';
import { scenariosDict } from '../support/dictionaries';
import {translateButton} from '../support/dictionaries';
import useRemoteController from '../remote/useRemoteController';
import RemoteConnectionPanel from '../remote/RemoteConnectionPanel';
export default function MainDisplay(props) {
    const { lang, remote = false } = props;
    const playerRef = useRef(null);
    const playerWrapperRef = useRef(null);
    const videoClickTimerRef = useRef(null);
    const [transitionFrame, setTransitionFrame] = useState('');
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [showVideoControls, setShowVideoControls] = useState(false);

    // print the lang in console
    console.log(lang);
    const {playPause, setPlayPause,
        curVideo, setCurVideo,
        scenarios, setScenarios,
        playSequence, setPlaySequence,
        declineBtn, setDeclineBtn,
        acceptBtn, setAcceptBtn,
        setCurrentlyPlaying,
        typeOfAnimation, setTypeOfAnimation,
    } = useHookContext();

    const preloadedVideo = scenarios.length ? scenariosDict[scenarios[0]] : '';
    const remoteConnection = useRemoteController(playerRef, curVideo, preloadedVideo, remote, lang);

    useEffect(() => {
        if (!preloadedVideo) {
            return undefined;
        }

        const preloadLink = document.createElement('link');
        preloadLink.rel = 'preload';
        preloadLink.as = 'video';
        preloadLink.href = preloadedVideo;
        document.head.appendChild(preloadLink);

        return () => {
            document.head.removeChild(preloadLink);
        };
    }, [preloadedVideo]);

    useEffect(() => () => {
        window.clearTimeout(videoClickTimerRef.current);
    }, []);

    const captureCurrentFrame = function() {
        const internalPlayer = playerRef.current?.getInternalPlayer?.();

        if (!(internalPlayer instanceof HTMLVideoElement)) {
            return;
        }

        if (!internalPlayer.videoWidth || !internalPlayer.videoHeight || internalPlayer.readyState < 2) {
            return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = internalPlayer.videoWidth;
        canvas.height = internalPlayer.videoHeight;

        const context = canvas.getContext('2d');
        if (!context) {
            return;
        }

        context.drawImage(internalPlayer, 0, 0, canvas.width, canvas.height);
        setTransitionFrame(canvas.toDataURL('image/jpeg', 0.92));
    }

    const swapVideo = function(nextVideo) {
        if (!nextVideo) {
            return;
        }

        if (nextVideo === curVideo) {
            setIsVideoLoading(false);
            setTransitionFrame('');
            playerRef.current?.seekTo(0, 'seconds');
            playerRef.current?.getInternalPlayer?.()?.play()?.catch(() => {});
            return;
        }
        captureCurrentFrame();
        setIsVideoLoading(true);
        setCurVideo(nextVideo);
    }

    const playNext = function() {
        resetReactions();
        if (scenariosDict[scenarios[0]] === scenariosDict[scenarios[1]]) {
            setPlayPause(true);
        }
        if (playPause) {
            swapVideo(scenariosDict['idle']);
            setPlayPause(false);
        } else {
            swapVideo(scenariosDict[scenarios[0]]);
            setScenarios(scenarios.slice(1));
        }
        setCurrentlyPlaying(true);
    }

    const playAll = function() {
        if (!scenarios.length) {
            return;
        }

        setPlaySequence(true);
        playNext();
    }

    const handleVideoClick = function() {
        if (showVideoControls) {
            return;
        }

        window.clearTimeout(videoClickTimerRef.current);
        videoClickTimerRef.current = window.setTimeout(playAll, 220);
    }

    const toggleVideoFullscreen = async function() {
        window.clearTimeout(videoClickTimerRef.current);
        const playerWrapper = playerWrapperRef.current;

        if (!playerWrapper) {
            return;
        }

        if (document.fullscreenElement) {
            await document.exitFullscreen();
        } else {
            await playerWrapper.requestFullscreen();
        }
    }
    
    const resetReactions = function() {
        setAcceptBtn('');
        setDeclineBtn('');
    }

    const reactToAnimation = function(reaction) {
        if (reaction === 'accept') {
            setAcceptBtn('accepted');
            setDeclineBtn('');
        } else {
            setAcceptBtn('');
            setDeclineBtn('declined');
        }
    }

    const typesOfAnimationList = [
        { value: 'face', label: translateButton('face', lang) },
        { value: 'body', label: translateButton('body', lang) },
        { value: 'count', label: translateButton('count', lang) },
        { value: 'room', label: translateButton('room', lang) },
        { value: 'bedroom', label: translateButton('bedroom', lang) },
        { value: 'bedroom_waking_up', label: translateButton('bedroom_waking_up', lang) },
        { value: 'kitchen1', label: translateButton('kitchen1', lang) },
        { value: 'kitchen2', label: translateButton('kitchen2', lang) },
        { value: 'kitchen3', label: translateButton('kitchen3', lang) },
        { value: 'garden', label: translateButton('garden', lang) }
    ];

    return (
        <>
        {remote && <RemoteConnectionPanel connection={remoteConnection} lang={lang} />}
        <main className='mainBox'>
            <section className='playerColumn' aria-label='Video player'>
                <div className='videoBox'>
                    <div
                        ref={playerWrapperRef}
                        className={`player-wrapper${showVideoControls ? '' : ' playerInteractive'}`}
                        onClick={handleVideoClick}
                        onDoubleClick={toggleVideoFullscreen}
                    >
                    {isVideoLoading && transitionFrame ? (
                        <img
                            className='videoTransitionFrame'
                            src={transitionFrame}
                            alt=''
                        />
                    ) : null}
                    <ReactPlayer
                        ref={playerRef}
                        className='react-player'
                        url= {curVideo}
                        fallback={scenariosDict['idle']}
                        width='100%'
                        height='100%'
                        controls={showVideoControls}
                        muted
                        playing
                        config={{
                            file: {
                                attributes: {
                                    preload: 'auto',
                                    playsInline: true,
                                },
                            },
                        }}
                        onPlay={() => {
                            setIsVideoLoading(false);
                            setTransitionFrame('');
                        }}
                        onEnded={() => {
                            setCurrentlyPlaying(false);
                            if (playSequence) {
                                if (scenarios.length) {
                                    playNext();
                                } else {
                                    swapVideo(scenariosDict.idle);
                                    setPlaySequence(false);
                                }
                            }
                        }}
                    />
                    <button
                        className='videoControlsToggle'
                        type='button'
                        aria-label='Video controls'
                        aria-pressed={showVideoControls}
                        title='Video controls'
                        onClick={(event) => {
                            event.stopPropagation();
                            setShowVideoControls(controlsVisible => !controlsVisible);
                        }}
                        onDoubleClick={event => event.stopPropagation()}
                    >
                        &#8942;
                    </button>
                    {preloadedVideo ? (
                        <video
                            className='videoPreload'
                            src={preloadedVideo}
                            preload='auto'
                            muted
                            playsInline
                            aria-hidden='true'
                            tabIndex={-1}
                        />
                    ) : null}
                    </div>

                </div>

                <div className='buttonGridMarks' role='group'>
                    <Button 
                        className={acceptBtn}
                        type='button' 
                        variant='success'
                        value='accept' 
                        size='md'
                        onClick={(value) => {
                            reactToAnimation(value.target.value);
                            swapVideo(scenariosDict.thumbsUp);
                        }}
                    >
                    {translateButton("Thumbs up",lang)}
                    </Button>

                    <Button 
                        className={declineBtn}
                        type='button' 
                        variant='danger'
                        value='decline' 
                        size='md'
                        onClick={(value) => {
                            reactToAnimation(value.target.value);
                            swapVideo(scenariosDict.no);
                        }}               
                        >
                    {translateButton("No",lang)}
                    </Button>
                </div>
            </section>
            <section className='scenarioColumn'>
                <div className='scenarioControls'>
                     <div className="buttonGridOptions primaryActions">
                <Button 
                    type='button' 
                    variant='primary'
                    value='Play next' 
                    size='md'
                    onClick={() => playNext()}
                >
                {translateButton("Play next",lang)}
                </Button>

                <Button 
                    type='button' 
                    variant='primary'
                    value='Play sequence' 
                    size='md'
                    onClick={() => playAll()}
                >
                {translateButton("Play sequence",lang)}
                </Button>
            </div>

                    <label>{translateButton("Select the type of scenarios:",lang)} </label>
                    <Select 
                        options = {typesOfAnimationList} 
                        className='setsSelector'
                        placeholder='Select...'
                        value={typesOfAnimationList.find(option => option.value === typeOfAnimation.value)}
                        onChange={value => {setTypeOfAnimation(value)}}
                    />
                    <TypeOfAnimationBlock lang={lang} />
                </div>
            </section>
            <aside className='queueColumn'>
                <label>{translateButton("Queue:",lang)}</label>
                    <Reorder.Group className='queueList' as='div' values={scenarios} onReorder={setScenarios}>
                        {scenarios.map((elem, idx) => (
                            <Reorder.Item className='queueItem' as='div' key={`${elem}-${idx}`} value={elem} drag='y'>
                                <Button
                                    className={`orderedItem`}
                                    type='none' 
                                    variant='info'
                                    value={elem + idx}
                                    size='md'
                                    onDoubleClick={() => {
                                        const result = [];
                                        for (const [index, scenario] of scenarios.entries()) {
                                            if (index !== idx) {
                                                result.push(scenario);
                                            }
                                        }
                                        console.log(elem);
                                        setCurrentlyPlaying(true);
                                        setScenarios(result);
                                    }}
                                    >
                                {
                                translateButton(elem,lang)
                                ///\d/.test(elem) ? elem.match(/\d/g).join('') : textToSigns(elem)
                                }
                                </Button>
                            </Reorder.Item>
                        ))}
                    </Reorder.Group>  
            </aside>
        </main>
        </>
    )
}
